import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Box, Button, Theme, Typography, useTheme, alpha, styled } from '@quillbot/design-system';
import { makeStyles } from '@mui/styles';
import { Check, CheckCircle, CheckCircleOutlined } from '@mui/icons-material';
import { PaymentScreens, PlanInterface, PriceContext } from 'src/payments/models/paymentFlow';
import { RootState } from 'src/_common/models/redux/store';
import { useSelector } from 'react-redux';
import { DEVICE_VIEWPORTS } from 'config/constants';
import { getSubscription } from 'src/payments/handlers/api';
import { Subscription } from 'src/payments/models/subscription';
import { PaymentProviders } from 'src/payments/models/payments';
import Track from 'src/payments/handlers/track';
import { PauseIcon, ThumbsUpIcon } from 'src/_common/quillElements/icon';
import ABTest from '@Common/hoc/ABTest';
import { openToastr } from '@Common/handlers/services';
import EmailVerifiedDialog from '../EmailVerifiedDialog';
import getConfig from 'next/config';
const { publicRuntimeConfig } = getConfig();

const StyledSave = styled('small')<{ isFeatured: boolean }>`
  font-weight: 700;
  font-size: 13px;
  display: flex;
  align-items: center;
  position: relative;
  color: ${({ theme, isFeatured }: { theme: Theme; isFeatured: boolean }) =>
    isFeatured ? theme.palette.secondary.main : theme.palette.primary.main};

  @media ${DEVICE_VIEWPORTS.xs} {
    margin-left: 0.5em;
    font-size: 16px;
  }
`;

const StyledSaveMobile = styled('small')<{ isFeatured: boolean }>`
  font-weight: 700;
  font-size: 13px;
  position: relative;
  color: ${({ theme, isFeatured }: { theme: Theme; isFeatured: boolean }) =>
    isFeatured ? theme.palette.secondary.main : theme.palette.primary.main};

  @media ${DEVICE_VIEWPORTS.xs} {
    margin-left: 0.5em;
    font-size: 16px;
  }
`;

const useStyles = makeStyles((theme) => ({
  dot: {
    display: 'none',
    [theme.breakpoints.down('sm')]: {
      height: '4px',
      width: '4px',
      display: 'block',
      background: '#C4C4C4',
      borderRadius: '39px',
      marginRight: '6px',
      border: '1px #C4C4C4 solid',
      padding: '1px',
      marginTop: '3px',
    },
  },

  planHolderBox: {
    backgroundColor: theme.palette.background.paper,
    margin: '0px 15px 15px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 20px',
    borderWidth: '2px',
    borderStyle: 'solid',
    height: '80px',
    maxWidth: '495px',
    '&::selection': {
      outline: 'none',
    },
    '&:focus': {
      outline: 'none',
    },
    [theme.breakpoints.down('md')]: {
      maxWidth: '495px',
    },

    [theme.breakpoints.down('sm')]: {
      width: '95%',
      padding: '20px 20px',
      margin: '0px auto 10px',
      minHeight: '98px',
      paddingLeft: '3em',
    },
  },
  planHolderBoxActive: {
    cursor: 'pointer',
  },
  planHolderFeaturedBorder: {
    borderColor: alpha(theme.palette.secondary.main, 0.45),
    '&:hover': {
      background: `${theme.palette.secondary.main}14`,
      // border: `4px solid ${theme.palette.secondary.dark}`,
    },

    ' & label': {},
  },
  planHolderBorder: {
    borderColor: 'rgba(76, 148, 90, 0.44)',
    '&:hover': {
      background: `${theme.palette.primary.dark}14`,
      // border: `4px solid ${theme.palette.primary.dark}`,
    },
  },
  planDetailsWrapper: {
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },

  planHolderStaticFeaturedBorder: {
    borderColor: theme.palette.secondary.main,
  },
  planHolderStaticBorder: {
    borderColor: 'rgba(76, 148, 90, 0.44)',
  },

  planTitleBox: {
    // minHeight: '60px',
    width: '120px',
    alignItems: 'center',
    flexDirection: 'column',
    justifyContent: 'center',
    textAlign: 'center',
    '& label': {
      fontWeight: 'bold',
      fontSize: '16px',
      position: 'relative',
      [theme.breakpoints.only('md')]: {
        fontSize: '100%',
      },
      [theme.breakpoints.down('md')]: {
        fontSize: '16px',
      },
    },

    [theme.breakpoints.down('sm')]: {
      width: '100%',
      flexDirection: 'row',
      minHeight: 'auto',
      alignItems: 'flex-start',
      justifyContent: 'flex-start',
    },
  },

  planTitleBoxActive: {
    cursor: 'pointer',
    '& label': {
      cursor: 'pointer',
    },
  },

  dummySpan: {
    height: '60px',
    width: '1px',
    borderLeft: '1px solid',
    borderColor: theme.palette.divider,
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  planInfoBox: {
    // minHeight: '60px',
    // width: '250px',
    alignItems: 'flex-start',
    paddingLeft: '15px',
    paddingRight: '15px',
    textAlign: 'left',
    justifyContent: 'center',
    flexDirection: 'column',
    // borderLeft: '1px #dcdcdc solid',
    position: 'relative',
    paddingTop: '3px',

    [theme.breakpoints.down('sm')]: {
      width: '100%',
      minHeight: 'auto',
      borderLeft: '0px',
      paddingLeft: '0px',
      paddingRight: '0px',
      paddingTop: '0px',
    },

    '& p': {
      '& small': {
        [theme.breakpoints.down('sm')]: {
          fontSize: '12px',
        },
      },
    },
  },

  planBtnBox: {
    // minHeight: '60px',
    width: '120px',
    textAlign: 'center',
    alignItems: 'center',
  },

  checkMobileIcon: {
    opacity: '0.45',
    fontSize: '32px',
  },

  planBoxPriceLabel: {
    fontWeight: 700,
    display: 'block',
    fontSize: '17px',
    marginBottom: '0px',
    marginTop: '2px',
    lineHeight: '12px',
    [theme.breakpoints.down('lg')]: {
      fontSize: '14px',
    },

    '& small': {
      fontSize: '12px',
      fontWeight: 600,
      [theme.breakpoints.down('sm')]: {
        fontSize: '12px',
      },
    },
  },

  plansContainer: {
    display: 'flex',
    // flexDirection: 'column',
    alignItems: 'center',
    marginBottom: 16,
    [theme.breakpoints.down('md')]: {
      // flexDirection: 'column-reverse',
    },
  },

  assuranceTitle: {
    textAlign: 'center',
    fontWeight: 600,
    fontSize: '14px',
    lineHeight: '19px',
    fontFamily: 'Open Sans',
  },
  assuranceDescription: {
    fontFamily: 'Open Sans',
    fontSize: '13px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '18px',
    letterSpacing: '0em',
    textAlign: 'center',
  },
}));

const StyledTextContainer = styled(Box)`
  && {
    padding-top: 16px;
    // color: #252525;
    @media ${DEVICE_VIEWPORTS.xs} {
      padding: 1em 2em;
    }

    p {
      margin-top: 8px;
      @media ${DEVICE_VIEWPORTS.xs} {
        margin-top: 10px;
      }
    }
  }
`;

const CircularButton = styled(Button)<{ featured: number }>`
  && {
    border-radius: 205px;
    font-weight: 600;
    padding-top: 0.7em;
    padding-bottom: 0.7em;
  }
`;

const SyledFeatureTypography = styled(Typography)`
  && {
    font-weight: 900;
    font-size: 12px;
    margin-top: -4px;
    margin-bottom: 4px;
    top: 0px;
    color: ${({ theme }: { theme: Theme }) => theme.palette.secondary.main};
    @media ${DEVICE_VIEWPORTS.lg} {
      margin-top: 0px;
      margin-bottom: 2px;
    }
    @media ${DEVICE_VIEWPORTS.md} {
      margin-top: 0px;
      margin-bottom: 2px;
    }
    @media ${DEVICE_VIEWPORTS.sm} {
      position: static;
    }
    @media ${DEVICE_VIEWPORTS.xs} {
      margin-top: 0px;
      margin-bottom: 2px;
    }
  }
`;

const StyledCheck = styled(Check)<{ featured: number }>`
  color: ${({ theme, featured }: { theme: Theme; featured: number }) =>
    featured ? theme.palette.secondary.main : theme.palette.primary.main};
  position: absolute;
  left: 0.9em;
`;

const StyledPlansContainer = styled('div')`
  overflow: hidden;
  height: 100%;
  padding: 28px 0px 0px;
  @media ${DEVICE_VIEWPORTS.md} {
    max-width: 500px;
    padding: 20px 8px 0px;
  }
  @media ${DEVICE_VIEWPORTS.sm} {
    max-width: 500px;
    padding: 20px 8px 0px;
  }
  @media ${DEVICE_VIEWPORTS.xs} {
    max-width: 500px;
    padding: 20px 8px 0px;
  }

  margin: auto;
  .card {
    margin-bottom: 15px;
  }
  .card:nth-child(1) {
    margin-top: 2px;
  }

  @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    height: inherit;
  }
`;

const parsePrice = (num: number) => {
  const front = num.toString().slice(0, 3);
  const back = num.toString().slice(3);

  return parseFloat(
    front + Math.ceil(parseFloat(`${back.slice(0, 1)}.${back.slice(1)}`)).toString()
  ).toFixed(2);
};

export default function Plan({
  setScreen,
  setShowEmailVerificationDialog,
}: {
  setScreen: React.Dispatch<
    React.SetStateAction<{
      screenIndex: PaymentScreens;
      selectedPlan: number;
    }>
  >;
  setShowEmailVerificationDialog: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  // const userPaymentTokens = useSelector((state: RootState) => state.pymt.tokens);
  const uid = useSelector((state: RootState) => state.user?.uid);
  const plans = useContext(PriceContext);
  const isPremiumUser = useSelector(
    (state: RootState) => state.user?.premium || state.user?.isSubPaused
  );
  const isEmailVerified = useSelector((state: RootState) => state.user.emailVerified);
  const isSyncWithDb = useSelector((state: RootState) => state.user?.syncedWithDb);
  const availedRefund = useSelector((state: RootState) => state.user?.availedRefund);
  const isMobile = useSelector((state: RootState) => state.common.platform.isMobile);
  const [selectedPlan, setSelectedPlan] = useState(-1);
  const theme = useTheme();
  // const classes = useStyles();

  const isIE = useSelector((store: RootState) => store.common.platform.isIE);
  const classes = useStyles({ isIE });

  useEffect(() => {
    if (isPremiumUser && uid)
      getSubscription().then((res: Subscription) => {
        if (res.type === PaymentProviders.PAYPAL) {
          let amount = Number(res.amount);
          amount = Math.round(amount * 100);
          switch (amount) {
            case 7995:
            case 3995:
              setSelectedPlan(3);
              break;
            case 2495:
            case 2995:
            case 5995:
              setSelectedPlan(2);
              break;
            default:
              setSelectedPlan(1);
          }
        } else {
          switch (res.interval) {
            case '12':
              setSelectedPlan(3);
              break;
            case '6':
              setSelectedPlan(2);
              break;
            default:
              setSelectedPlan(1);
          }
        }
      });
  }, [isSyncWithDb]);

  const gotoPaymentScreenWithPlan = (planID: number, price: number) => {
    if (isPremiumUser) return;
    // if user not logged in, show sign up
    if (!uid) {
      Track.selectPlan(planID, price);
      setScreen({
        selectedPlan: planID,
        screenIndex: PaymentScreens.SIGNUP,
      });
    } else if (
      !isEmailVerified &&
      ['qa', 'test', 'stage', 'prod', 'production'].includes(publicRuntimeConfig.QUILLBOT_ENV)
    ) {
      // if user logged in but has not verified email
      Track.unverifiedSelectPlan(planID, price);
      setShowEmailVerificationDialog(true);
    } else {
      // if logged in and has verified email
      Track.selectPlan(planID, price);
      setScreen({
        selectedPlan: planID,
        screenIndex: PaymentScreens.CHECKOUT,
      });
    }
  };

  const CicleCard = (props: PlanInterface) => {
    const classNames = [classes.planHolderBox];
    if (isPremiumUser) {
      if (props.featured) classNames.push(classes.planHolderStaticFeaturedBorder);
      else classNames.push(classes.planHolderStaticBorder);
    } else {
      if (props.featured) classNames.push(classes.planHolderFeaturedBorder);
      else classNames.push(classes.planHolderBorder);
      classNames.push(classes.planHolderBoxActive);
    }
    return (
      <>
        <Box
          display="flex"
          className={classNames.join(' ')}
          borderRadius="50px"
          boxShadow={isPremiumUser ? 0 : 3}
          onClick={() => gotoPaymentScreenWithPlan(props.id, props.price)}
        >
          <div className={classes.planDetailsWrapper}>
            <Box
              display="flex"
              className={`${classes.planTitleBox} ${
                isPremiumUser ? '' : classes.planTitleBoxActive
              }`}
            >
              <label>{props.title}</label>
              {props.saving && (
                <StyledSave isFeatured={!!props.featured}>
                  <span className={classes.dot}></span>Save {props.saving}%
                </StyledSave>
              )}
            </Box>
            <span className={classes.dummySpan}></span>
            <Box className={classes.planInfoBox}>
              {!!props.featured && !isMobile && (
                <SyledFeatureTypography variant="h2">Best Value</SyledFeatureTypography>
              )}
              <div style={{ width: '100%' }}>
                <Typography className={classes.planBoxPriceLabel}>
                  ${props.duration === 1 ? props.price : props.monthlyPrice} USD
                  <span>
                    <small
                      style={{
                        marginLeft: '5px',
                      }}
                    >
                      {props.duration === 1 ? 'billed monthly' : 'per month'}
                    </small>
                    {props.saving && props.featured && isMobile && (
                      <StyledSaveMobile isFeatured={!!props.featured}>
                        (Best Value)
                      </StyledSaveMobile>
                    )}
                  </span>
                </Typography>
              </div>
              {!!props.monthlyPrice && (
                <div style={{ width: '100%' }}>
                  <span>
                    <small
                      style={{
                        // color: '#252525',
                        fontSize: '12px',
                        fontWeight: 600,
                      }}
                    >
                      ${props.price}{' '}
                      {props.duration === 1
                        ? ' billed every month'
                        : ` billed every ${props.duration} months`}
                    </small>
                  </span>
                </div>
              )}
            </Box>
            {/* {props.id === selectedPlan && isMobile && (
                <StyledCheck featured={props.featured ? 1 : 0} />
              )} */}
          </div>

          {(!isPremiumUser || props.id === selectedPlan) &&
            (isMobile ? (
              selectedPlan && isPremiumUser ? (
                <CheckCircle color="primary" style={{ fontSize: '32px' }} />
              ) : (
                <CheckCircleOutlined
                  color={props.featured ? 'secondary' : 'primary'}
                  className={classes.checkMobileIcon}
                />
              )
            ) : (
              <Box display="flex" className={classes.planBtnBox}>
                <CircularButton
                  color={props.featured ? 'secondary' : 'primary'}
                  variant="contained"
                  fullWidth
                  disableElevation
                  disabled={props.id === selectedPlan && isPremiumUser}
                  featured={props.featured ? 1 : 0}
                >
                  {props.id === selectedPlan && isPremiumUser ? 'Current' : 'Select'}
                </CircularButton>
              </Box>
            ))}
        </Box>
      </>
    );
  };

  return (
    <StyledPlansContainer>
      <ABTest experiment="plansOrder_May2022" default>
        <Box flexDirection="column-reverse" className={classes.plansContainer}>
          {useMemo(
            () => [...plans].splice(1).map((plan) => <CicleCard {...plan} key={plan.id} />),
            [
              plans,
              uid,
              selectedPlan,
              isPremiumUser,
              theme,
              // userPaymentTokens,
            ]
          )}
        </Box>
      </ABTest>
      <ABTest experiment="plansOrder_May2022" variant="A">
        <Box
          flexDirection={{ xs: 'column-reverse', md: 'column' }}
          className={classes.plansContainer}
        >
          {useMemo(
            () => [...plans].splice(1).map((plan) => <CicleCard {...plan} key={plan.id} />),
            [
              plans,
              uid,
              selectedPlan,
              isPremiumUser,
              theme,
              // userPaymentTokens,
            ]
          )}
        </Box>
      </ABTest>
      <ABTest experiment="plansOrder_May2022" variant="B">
        <Box flexDirection="column-reverse" className={classes.plansContainer}>
          {useMemo(
            () => [...plans].splice(1).map((plan) => <CicleCard {...plan} key={plan.id} />),
            [
              plans,
              uid,
              selectedPlan,
              isPremiumUser,
              theme,
              // userPaymentTokens,
            ]
          )}
        </Box>
      </ABTest>

      {!availedRefund && (
        <StyledTextContainer style={{ textAlign: 'center' }}>
          <Typography className={classes.assuranceTitle} variant={'h6'}>
            100% Money-Back Guarantee <ThumbsUpIcon />
          </Typography>
          <p className={classes.assuranceDescription}>
            Changed your mind? We’ll refund your payment within 3 days of purchase.
          </p>
        </StyledTextContainer>
      )}

      <StyledTextContainer style={{ textAlign: 'center' }}>
        <Typography className={classes.assuranceTitle} variant={'h6'}>
          Pause Your Subscription <PauseIcon />
        </Typography>
        <p className={classes.assuranceDescription}>
          Taking a break? Your subscription also pauses when you do!
        </p>
      </StyledTextContainer>
    </StyledPlansContainer>
  );
}
